import React, { useRef, useState, useEffect } from "react";
import jsPDF from "jspdf";
import { X, Eraser, PenLine, Download, Loader2, Type, FileSignature, Plus, Share2, Check, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { helperSaveSigned, helperMergePdf } from "../lib/api";

export default function DocumentSigner({ title: initTitle, body: initBody, attachments = [], pdfAttachments = [], signerName = "Sohel Patel", userId, initFolder = "", folders = [], onSaved, onClose }) {
  const [title, setTitle] = useState(initTitle || "Document");
  const [body, setBody] = useState(initBody || "");
  const [name, setName] = useState(signerName);
  const [place, setPlace] = useState("");
  const [keywords, setKeywords] = useState("");
  const [folder, setFolder] = useState(initFolder || "");
  const [date, setDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; });
  const [includeFiles, setIncludeFiles] = useState(false);
  const prettyDate = (() => { const [yy, mm, dd] = String(date).split("-").map(Number); return yy && mm && dd ? new Date(yy, mm - 1, dd).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : date; })();
  const [mode, setMode] = useState("draw");
  const [building, setBuilding] = useState(false);
  const [extraLine, setExtraLine] = useState("");
  const bodyRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const imgCount = attachments.filter((a) => a.isImage && a.dataUrl).length;

  useEffect(() => {
    if (mode !== "draw") return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0b3d91";
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
    const down = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); hasInk.current = true; e.preventDefault(); };
    const up = () => { drawing.current = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", up);
    return () => { c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move); window.removeEventListener("touchend", up); };
  }, [mode]);

  const clearSig = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); hasInk.current = false; };
  const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

  // Insert a custom line into the body at the cursor (or append) — "add info when I feel like"
  const addLine = () => {
    const line = extraLine.trim();
    if (!line) return;
    const ta = bodyRef.current;
    if (ta && typeof ta.selectionStart === "number") {
      const p = ta.selectionStart;
      const next = body.slice(0, p) + (p > 0 && body[p - 1] !== "\n" ? "\n" : "") + line + "\n" + body.slice(p);
      setBody(next);
      requestAnimationFrame(() => { try { ta.focus(); const np = p + line.length + 2; ta.setSelectionRange(np, np); } catch (e) {} });
    } else {
      setBody((b) => (b ? b.replace(/\s*$/, "") + "\n" : "") + line);
    }
    setExtraLine("");
  };

  const buildBlob = async () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
    const M = 56; let y = M;
    doc.setFont("times", "bold"); doc.setFontSize(16);
    const tl = doc.splitTextToSize(title, W - 2 * M);
    doc.text(tl, W / 2, y, { align: "center" }); y += tl.length * 20 + 6;
    doc.setDrawColor(150); doc.line(M, y, W - M, y); y += 26;
    doc.setFont("times", "normal"); doc.setFontSize(11);
    doc.text(`Date: ${prettyDate}${place ? "     Place: " + place : ""}`, M, y); y += 24;
    const lines = doc.splitTextToSize(body || "", W - 2 * M);
    for (const ln of lines) { if (y > H - M - 130) { doc.addPage(); y = M; } doc.text(ln, M, y); y += 16; }
    if (y > H - M - 140) { doc.addPage(); y = M; }
    y += 26; doc.text("Signed electronically:", M, y);
    if (mode === "draw") {
      const sig = canvasRef.current.toDataURL("image/png");
      doc.addImage(sig, "PNG", M, y + 8, 180, 64); y += 78;
    } else {
      doc.setFont("times", "italic"); doc.setFontSize(24); doc.text(name || "", M, y + 38);
      doc.setFont("times", "normal"); doc.setFontSize(11); y += 60;
    }
    doc.setDrawColor(150); doc.line(M, y, M + 220, y); y += 15;
    doc.setFontSize(11); doc.text(name || "", M, y); y += 15;
    doc.setTextColor(120); doc.setFontSize(9);
    doc.text(`Electronically signed on ${prettyDate}${place ? " at " + place : ""}`, M, y);
    doc.setTextColor(0); doc.setFontSize(11);
    for (const a of (includeFiles ? attachments : []).filter((x) => x.isImage && x.dataUrl)) {
      try {
        const img = await loadImg(a.dataUrl);
        doc.addPage();
        doc.setFont("times", "bold"); doc.setFontSize(12);
        doc.text(`Attachment: ${a.name}`, M, M); doc.setFont("times", "normal");
        const maxW = W - 2 * M, maxH = H - 2 * M - 30;
        let iw = img.width, ih = img.height; const scale = Math.min(maxW / iw, maxH / ih); iw *= scale; ih *= scale;
        const fmt = a.dataUrl.includes("image/png") ? "PNG" : "JPEG";
        doc.addImage(a.dataUrl, fmt, M + (maxW - iw) / 2, M + 20, iw, ih);
      } catch (e) { /* skip bad image */ }
    }
    const fname = (title || "document").replace(/[^a-z0-9]+/gi, "_").slice(0, 50) + "_signed.pdf";
    // Discreet brand footer on every page.
    const pageCount = doc.internal.getNumberOfPages();
    for (let pi = 1; pi <= pageCount; pi++) {
      doc.setPage(pi);
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text("Powered by A V Tech", W / 2, H - 14, { align: "center" });
      doc.setTextColor(0);
    }
    return { blob: doc.output("blob"), fname };
  };

  const finish = async (share) => {
    if (mode === "draw" && !hasInk.current) { toast.message("Please draw your signature first (or switch to Type)."); return; }
    if (!body.trim()) { toast.message("The document is empty — type or paste what you want in the PDF."); return; }
    setBuilding(true);
    try {
      let { blob, fname } = await buildBlob();
      // Merge any real PDF attachments (proforma/contract) after the signed letter (server-side).
      if (includeFiles && pdfAttachments && pdfAttachments.length && userId) {
        try { blob = await helperMergePdf(userId, blob, pdfAttachments.map((a) => a.file).filter(Boolean)); }
        catch (e) { toast.message("Couldn't merge the attached PDF(s) — sending your signed letter on its own."); }
      }
      // Save a copy to the searchable archive (best-effort — never blocks the share/download).
      let archived = false;
      try {
        if (userId) { await helperSaveSigned(userId, { title, signer: name, date, place, keywords, folder }, blob); archived = true; onSaved?.(); }
      } catch (e) { toast.message("Couldn't save to Signed Docs — it won't be searchable, but your PDF is ready."); }

      const file = new File([blob], fname, { type: "application/pdf" });
      if (share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title }); toast.success(archived ? "Saved to Signed Docs — pick WhatsApp to forward" : "Ready — pick WhatsApp to forward"); onClose?.(); return; }
        catch (e) { /* user cancelled share — fall through to download */ }
      }
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = fname; a.click(); URL.revokeObjectURL(a.href);
      toast.success(archived ? "Signed PDF saved to Signed Docs + downloaded" : "Signed PDF downloaded");
      onClose?.();
    } catch (e) { toast.error("Could not build the PDF"); }
    finally { setBuilding(false); }
  };

  const inp = "w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00F0FF]";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="doc-signer">
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-[#071018] border border-[#00F0FF44] rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="nx-orbitron font-black tracking-widest text-[#00F0FF] text-sm flex items-center gap-2"><FileSignature className="w-4 h-4" /> DIGISIGN</h3>
            <p className="text-cyan-100/45 text-[10px] nx-mono mt-0.5 ml-6">Powered by A V Tech</p>
          </div>
          <button onClick={onClose} data-testid="signer-close" className="text-cyan-200/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div>
          <label className="text-cyan-200/50 text-[11px] nx-mono">DOCUMENT TITLE (PDF header)</label>
          <input className={inp} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="signer-title" placeholder="e.g. Sponsor Letter for Shabana Patel (Mother)" />
        </div>

        {/* Editable body — only the info YOU put in ends up in the PDF */}
        <div>
          <label className="text-cyan-200/50 text-[11px] nx-mono">DOCUMENT TEXT — edit freely, only this goes in the PDF</label>
          <textarea ref={bodyRef} className={`${inp} font-sans leading-relaxed`} rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="signer-body" placeholder="Type or paste exactly what you want in the document…" />
          <div className="flex items-center gap-2 mt-2">
            <input className={inp + " flex-1"} value={extraLine} onChange={(e) => setExtraLine(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLine(); } }} data-testid="signer-add-line-input" placeholder="Insert a line / detail (e.g. Passport No: A1234567)" />
            <button onClick={addLine} data-testid="signer-add-line" className="px-3 py-2 rounded-lg nx-mono text-[11px] text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1 whitespace-nowrap"><Plus className="w-3.5 h-3.5" /> Add line</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-cyan-200/50 text-[11px] nx-mono">SIGNER NAME</label><input className={inp} value={name} onChange={(e) => setName(e.target.value)} data-testid="signer-name" /></div>
          <div><label className="text-cyan-200/50 text-[11px] nx-mono">PLACE (optional)</label><input className={inp} value={place} onChange={(e) => setPlace(e.target.value)} placeholder="City" data-testid="signer-place" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-cyan-200/50 text-[11px] nx-mono">DATE</label><input type="date" className={inp} value={date} onChange={(e) => setDate(e.target.value)} data-testid="signer-date" /></div>
          <div><label className="text-cyan-200/50 text-[11px] nx-mono">TAGS (to find it later)</label><input className={inp} value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="sponsor, mother, visa" data-testid="signer-keywords" /></div>
        </div>
        <div>
          <label className="text-cyan-200/50 text-[11px] nx-mono flex items-center gap-1"><FolderOpen className="w-3 h-3" /> FOLDER (file it away — optional)</label>
          <input className={inp} value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="e.g. Visa, Property, Business" list="signer-folder-list" data-testid="signer-folder" />
          <datalist id="signer-folder-list">{folders.map((f) => <option key={f.name} value={f.name} />)}</datalist>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="text-cyan-200/50 text-[11px] nx-mono">YOUR SIGNATURE</label>
            <div className="flex gap-1 ml-auto">
              <button onClick={() => setMode("draw")} data-testid="signer-mode-draw" className={`px-2 py-1 rounded-md text-[10px] nx-mono flex items-center gap-1 ${mode === "draw" ? "bg-[#00F0FF] text-[#030712]" : "text-cyan-200/60 border border-cyan-200/25"}`}><PenLine className="w-3 h-3" /> Draw</button>
              <button onClick={() => setMode("type")} data-testid="signer-mode-type" className={`px-2 py-1 rounded-md text-[10px] nx-mono flex items-center gap-1 ${mode === "type" ? "bg-[#00F0FF] text-[#030712]" : "text-cyan-200/60 border border-cyan-200/25"}`}><Type className="w-3 h-3" /> Type</button>
            </div>
          </div>
          {mode === "draw" ? (
            <div className="relative">
              <canvas ref={canvasRef} width={440} height={150} data-testid="signer-canvas"
                className="w-full rounded-lg bg-white touch-none" style={{ touchAction: "none" }} />
              <button onClick={clearSig} data-testid="signer-clear" className="absolute top-1.5 right-1.5 px-2 py-1 rounded-md bg-black/10 text-[10px] text-slate-700 flex items-center gap-1"><Eraser className="w-3 h-3" /> Clear</button>
            </div>
          ) : (
            <div className="rounded-lg bg-white h-[90px] flex items-center px-4"><span style={{ fontFamily: "'Brush Script MT', cursive", fontSize: 34, color: "#0b3d91" }}>{name || "Your name"}</span></div>
          )}
        </div>

        {(imgCount > 0 || (pdfAttachments && pdfAttachments.length > 0)) && (
          <label className="flex items-center gap-2 text-cyan-100/70 text-[11px] nx-mono" data-testid="signer-include-files">
            <input type="checkbox" checked={includeFiles} onChange={(e) => setIncludeFiles(e.target.checked)} data-testid="signer-include-files-checkbox" />
            Also add my uploaded files ({imgCount + (pdfAttachments?.length || 0)}) as extra pages. Off by default: they're only used to fill in details.
          </label>
        )}

        <div className="grid grid-cols-1 gap-2">
          <button onClick={() => finish(true)} disabled={building} data-testid="signer-whatsapp"
            className="w-full py-3 rounded-xl nx-orbitron font-bold tracking-wide text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg,#25D366,#128C7E)" }}>
            {building ? <><Loader2 className="w-5 h-5 animate-spin" /> Compiling…</> : <><Share2 className="w-5 h-5" /> Sign, Save & Send via WhatsApp</>}
          </button>
          <button onClick={() => finish(false)} disabled={building} data-testid="signer-create"
            className="w-full py-2.5 rounded-xl nx-mono text-xs font-bold tracking-wide text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] disabled:opacity-40 flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Sign, Save & Download PDF
          </button>
        </div>
        <p className="text-cyan-100/35 text-[10px] nx-mono text-center flex items-center justify-center gap-1"><Check className="w-3 h-3 text-[#25D366]" /> A copy is kept in <b className="text-cyan-100/60">Signed Docs</b> so you can find & re-share it anytime. On phone the WhatsApp button opens Share → pick your chat/group.</p>
      </div>
    </div>
  );
}
